<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import SectionTitle from "./SectionTitle.vue";
import FieldError from "./FieldError.vue";
import prices from "./Prices.vue";
import ErrorWrapper from "./ErrorWrapper.vue";

const props = defineProps({
  errors: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: [Object, null],
    required: true,
    default: null,
  },
  attraction: {
    type: Object,
    default: null
  },
  prices: {
    type: Array,
    default: () => []
  },
  availability: {
    type: Object,
    default: () => {}
  },
  number: {
    type: Number,
    default: 2
  },
  time: {
    type: [String, Boolean],
    default: false
  },
  submitted: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const hasAttraction = computed(() => {
  return !props.attraction?.id ?? false;
})

const hasPrices = computed(() => {
  return props.availability?.priceOptions && Object.keys(props.availability.priceOptions).length > 0 && !!props.time;
})

const participants = ref({});

const total = computed(() => {
  return Object.values(participants.value).reduce((total, value) => total + value, 0);
});

const warning = computed(() => {
  return total.value > 0 && participants.value.adults < 1 && participants.value.seniors < 1;
});

const climbWarning = computed(() => {
  return props.attraction === 5 && participants.value?.infants && participants.value?.infants > 0;
})

const remove = async () => {
  let current = participants.value;

  for (const currentKey in current) {
    current[currentKey] = 0;
  }

  participants.value = current;

  change()
}

const clearParticipants = async () => {
  let current = {}

  if (props.availability?.priceOptions){
    for (const price of props.availability.priceOptions) {
      if (Object.hasOwn(participants.value, price.label) && participants.value[price.label]){
        current[price.label] = participants.value[price.label];
      } else {
        current[price.label] = 0;
      }
    }
  }

  participants.value = current;

  change()
}

const totalSeatsUsed = computed(() => {
  let total = 0;

  if (!props.availability?.priceOptions){
    return total;
  }
  // Object.keys(participants.value)

  for (const [key, label] of Object.keys(participants.value).entries()) {
    if (props.availability?.priceOptions?.[key]?.seatsUsed && participants.value?.[label]){
      total += props.availability.priceOptions[key].seatsUsed * participants.value[label]
    }
  }

  return total;
});

const seatsAvailable = computed(() => {
  return props.availability?.seatsAvailable ?? 0;
});

watch(() => props.modelValue, (newValue) => {
  participants.value = newValue;
}, { deep: true });

watch(() => props.availability, () => {
  clearParticipants();
}, { deep: true });

onMounted(() => {
  clearParticipants();
});

const change = () => {
  emit('update:modelValue', participants.value);
  emit('change', participants.value);
};

const set = (type, amount) => {
  participants.value[type] += amount;
  change();
};

const ticketTypes = [
    {
      label: 'Adults',
      key: 'adults'
    }, {
      label: 'Children',
      key: 'children'
    }, {
      label: 'Seniors',
      key: 'seniors'
    }, {
      label: 'Infants',
      key: 'infants'
    }, {
      label: 'Spectator',
      key: 'spectator'
    }
];

// Extract label from the ticket object structure
const getLabelFromTicket = (ticket) => {
  // Handle reactive structure from priceOptions
  if (ticket._custom?.value?.label) {
    return ticket._custom.value.label.toLowerCase();
  }
  // Handle direct label property
  if (ticket.label) {
    return ticket.label.toLowerCase();
  }
  // Handle other possible structures
  if (ticket.key) {
    return ticket.key.toLowerCase();
  }

  return '';
};

const filteredTicketTypes = computed(() => {
  let tickets = [];
  if (props.availability?.priceOptions && props.availability.priceOptions.length){
    tickets = props.availability.priceOptions;
  } else if (props.attraction?.ticket_types && props.attraction.ticket_types.length){
    tickets = ticketTypes.filter(tt => props.attraction.ticket_types.includes(tt.key));
  } else {
    tickets = ticketTypes.filter(tt => tt.key !== 'spectator');
  }

  // Define the preferred order
  const orderMap = {
    'adult': 1, 'adults': 1,
    'child': 2, 'children': 2,
    'senior': 3, 'seniors': 3,
    'infant': 4, 'infants': 4,
    'spectator': 5, 'spectators': 5
  };

  // Sort tickets based on the preferred order
  return tickets.sort((a, b) => {

    const labelA = getLabelFromTicket(a);
    const labelB = getLabelFromTicket(b);

    const orderA = orderMap[labelA] || 999; // Unknown types go to the end
    const orderB = orderMap[labelB] || 999;

    // If both have the same order priority, sort alphabetically
    if (orderA === orderB) {
      return labelA.localeCompare(labelB);
    }

    return orderA - orderB;
  });
})

const resolveDesc = (ticket) => {
  let translate = {
    "Adult": 'adults',
    "Child": 'children',
    "Seniors": 'seniors',
    "Senior": 'seniors',
    "Infants": 'infants',
    "Infant": 'infants',
    "Spectator": 'spectators',
    "Spectators": 'spectators',
  }

  let key = Object.hasOwn(translate, ticket.label) ? translate[ticket.label] : ticket.label;
  let desc = props.attraction?.ticket_desc?.[key] ? props.attraction?.ticket_desc?.[key] : ''

  return desc + ' ' + (ticket.price ? `$${ticket.price.toFixed(2)}` : '')
}

</script>
<template>
  <div class="mb-2">

    <SectionTitle :number="number" class="mt-12 lg:mt-20">
      Tickets
    </SectionTitle>
    <div class="relative">
      <error-wrapper :submitted="submitted" :errors="errors">
        <div v-if="!hasPrices" class="absolute -inset-4 z-20 backdrop-blur-sm bg-light/30"></div>


        <div class="flex items-justified-center min-h-6"
             :class="{'justify-between' : attraction, 'justify-end' : !attraction}">

          <span v-if="attraction" class="flex gap-2 items-center justify-start font-bold text-2xl mb-4">
            <svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.80598 9.09186L15.4459 1.21686C16.5115 0.601624 17.9042 0.974798 18.5194 2.04042L19.5917 3.89763C19.7675 4.2021 19.7126 4.59916 19.4913 4.8487C19.0359 5.39581 18.9565 6.17236 19.308 6.78128C19.6772 7.42065 20.3894 7.74017 21.0909 7.6193C21.4176 7.55244 21.789 7.70341 21.9647 8.00787L23.0194 9.83464C23.6522 10.9307 23.2615 12.293 22.1959 12.9082L8.55598 20.7832C7.45992 21.416 6.11523 21.0557 5.48242 19.9596L4.42773 18.1329C4.25195 17.8284 4.27643 17.4489 4.52815 17.1818C4.95314 16.6523 5.06298 15.8581 4.69384 15.2188C4.34228 14.6099 3.59962 14.3079 2.92855 14.4112C2.57135 14.4956 2.23047 14.3271 2.05469 14.0226L0.982422 12.1654C0.367188 11.0998 0.709916 9.72467 1.80598 9.09186ZM2.44384 11.3217L3.23486 12.6917C4.38618 12.7171 5.54002 13.3094 6.15526 14.375C6.78807 15.4711 6.72407 16.7665 6.1704 17.7763L6.94384 19.1159C7.10204 19.3899 7.43821 19.48 7.71223 19.3218L21.3521 11.4468C21.5957 11.3062 21.7162 10.9524 21.558 10.6784L20.7846 9.33876C19.6028 9.33094 18.4794 8.72109 17.8466 7.62503C17.2314 6.55941 17.2649 5.2816 17.849 4.25424L17.058 2.88417C16.9174 2.6406 16.5332 2.53765 16.2896 2.67828L2.64973 10.5533C2.37571 10.7115 2.30321 11.0781 2.44384 11.3217ZM16.2547 10.4927L14.5672 7.56986L7.7472 11.5074L9.4347 14.4302L16.2547 10.4927ZM6.00454 11.864C5.70571 11.3464 5.86828 10.6436 6.41631 10.3272L14.2105 5.82719C14.7281 5.52837 15.4485 5.72139 15.7473 6.23897L17.9973 10.1361C18.3137 10.6841 18.1031 11.374 17.5855 11.6729L9.79131 16.1729C9.24328 16.4893 8.57094 16.3091 8.25454 15.7611L6.00454 11.864Z"
                    fill="currentColor"/>
            </svg>
            {{ attraction.retail_title }}
          </span>

          <button v-if="total > 0" @click.prevent="remove" class="flex min-w-44 items-center justify-center gap-2 text-dark underline">
            Remove all
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4">
              <path d="M2.38462 0.999999L7 5.61538L11.6154 1C11.9904 0.624999 12.625 0.625 13 1C13.4038 1.40385 13.375 2.00961 13 2.38462L8.38462 7L13 11.6154C13.4038 12.0192 13.375 12.625 13 13C12.5962 13.4038 12.0192 13.4038 11.6154 13L7 8.38462L2.38462 13C1.98077 13.4038 1.40385 13.4038 1 13C0.625001 12.625 0.596154 12.0192 1 11.6154L5.61539 7L1 2.38461C0.625001 2.00961 0.596154 1.40385 1 1C1.375 0.624999 2.00962 0.624999 2.38462 0.999999Z"
                    fill="currentColor"/>
            </svg>
          </button>

        </div>
        <div
            class="flex justify-between items-center border-b border-dotted border-primary py-4"
            v-for="ticketType in filteredTicketTypes"
            data-testid="booking-participant-control"
        >
          <div class="flex flex-col sm:flex-row gap-2 items-baseline text-lg lg:text-xlx">
            <strong class="font-bold" v-text="ticketType.label"></strong>
            <span v-text="resolveDesc(ticketType)" class="font-medium	text-mdx lg:text-lg"></span>
          </div>
          <div class="flex gap-2 items-center">
            <button
                data-testid="booking-participant-minus"
                class="bg-light text-dark h-12 w-12 rounded-full flex items-center justify-center"
                :class="{
                  'opacity-50' : participants[ticketType.label] <= 0,
                }"
                :disabled="participants[ticketType.label] <= 0"
                @click.prevent="participants[ticketType.label] > 0 ? set(ticketType.label, -1) : null"
            >
              <svg width="14" height="2" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.5 1C13.5 1.5625 13.0312 2 12.5 2H1.5C0.9375 2 0.5 1.5625 0.5 1C0.5 0.46875 0.9375 0 1.5 0H12.5C13.0312 0 13.5 0.46875 13.5 1Z"
                      fill="currentColor"/>
              </svg>
            </button>
            <strong class="min-w-6 text-xl font-bold text-center leading-none">{{ participants[ticketType.label] }}</strong>
            <button
                data-testid="booking-participant-plus"
                class="bg-light text-dark h-12 w-12 rounded-full flex items-center justify-center"
                :disabled="totalSeatsUsed >= seatsAvailable"
                @click.prevent="set(ticketType.label, 1)"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1.5V6H12.5C13.0312 6 13.5 6.46875 13.5 7C13.5 7.5625 13.0312 8 12.5 8H8V12.5C8 13.0625 7.53125 13.5 7 13.5C6.4375 13.5 6 13.0625 6 12.5V8H1.5C0.9375 8 0.5 7.5625 0.5 7C0.5 6.46875 0.9375 6 1.5 6H6V1.5C6 0.96875 6.4375 0.5 7 0.5C7.53125 0.5 8 0.96875 8 1.5Z"
                      fill="currentColor" />
              </svg>
            </button>
          </div>
        </div>

        <div v-if="warning" class="text-orange text-sm mt-2">
          Warning: Children under 17 must be accompanied by an adult.
        </div>

        <div v-if="climbWarning" class="bg-tertiary-light p-4 rounded text-sm mx-6 mb-2">
          Due to regulations you need to be 4 years old to climb Cape Leeuwin Lighthouse
        </div>

        <FieldError v-if="submitted" :errors="errors" />
      </error-wrapper>
    </div>
  </div>
</template>
